// @spec SHL-STR-019

/**
 * Event name for search result click analytics.
 */
export const SEARCH_RESULT_CLICK_EVENT = 'search:result:click';

/**
 * Data included with the search result click event.
 */
export interface SearchResultClickData {
  /** The search query that was executed */
  query: string;
  /** ID of the clicked result */
  resultId: string;
  /** Type/category of the result (e.g., 'members', 'organizations') */
  resultType: string;
  /** URL of the clicked result */
  resultUrl: string;
  /** 0-indexed position of the result in the list */
  position: number;
  /** Whether result was opened in new tab */
  newTab: boolean;
}

/**
 * Track a search result click for analytics.
 *
 * Dispatches a CustomEvent on the window object that can be captured
 * by analytics integrations (e.g., Google Analytics, Mixpanel, etc.)
 * without coupling to any specific provider.
 *
 * @param data - Data about the clicked result
 *
 * @example
 * // Track a click
 * trackSearchResultClick({
 *   query: 'john',
 *   resultId: 'user-123',
 *   resultType: 'members',
 *   resultUrl: '/settings/team/123',
 *   position: 0,
 *   newTab: false,
 * });
 *
 * // Listen for events (e.g., in analytics setup)
 * window.addEventListener('search:result:click', (event) => {
 *   const detail = (event as CustomEvent<SearchResultClickData>).detail;
 *   analytics.track('search_result_click', detail);
 * });
 */
export function trackSearchResultClick(data: SearchResultClickData): void {
  if (typeof window === 'undefined') return;

  const event = new CustomEvent<SearchResultClickData>(SEARCH_RESULT_CLICK_EVENT, {
    detail: data,
    bubbles: true,
    cancelable: false,
  });

  window.dispatchEvent(event);
}
